import { useEffect, useState } from 'react';
import type { GameSocketApi } from './hooks/useGameSocket';
import { useGameStore, useRoomCode, useSnapshot } from './store/gameStore';
import { MAX_PLAYERS, TURN_TIME_OPTIONS } from '../../shared/types';
import { COLOR_LABELS } from '../../shared/board';
import type { Color } from '../../shared/board';
import { roomShareUrl } from './router';
import { GuestBadge } from './components/GuestBadge';
import { ACCOUNT_URL, ensureSession, renameGuest, SessionApiError, type SessionInfo } from './auth/session';

export default function Lobby({ socket }: { socket: GameSocketApi }) {
  const connected = useGameStore((s) => s.connected);
  const myId = useGameStore((s) => s.myPlayerId);
  const snapshot = useSnapshot();
  const roomCode = useRoomCode();
  const error = useGameStore((s) => s.error);

  const pendingJoinCode = useGameStore((s) => s.pendingJoinCode);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [joinCode, setJoinCode] = useState(pendingJoinCode || '');

  useEffect(() => {
    if (pendingJoinCode) setJoinCode(pendingJoinCode);
  }, [pendingJoinCode]);

  useEffect(() => {
    let active = true;
    void ensureSession().then((resolved) => {
      if (active) setSession(resolved);
    });
    return () => {
      active = false;
    };
  }, []);

  const inRoom = !!snapshot && !!myId && snapshot.players.some((p) => p.id === myId);

  if (!inRoom) {
    return (
      <Home
        session={session}
        setSession={setSession}
        joinCode={joinCode}
        setJoinCode={setJoinCode}
        socket={socket}
        connected={connected}
        error={error}
      />
    );
  }

  return <RoomLobby socket={socket} roomCode={roomCode} snapshot={snapshot!} myId={myId!} error={error} />;
}

function ArenaBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
        }}
      />
      <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-[#ff5a5a]/20 blur-3xl snk-float-slow" />
      <div className="absolute top-1/4 -right-28 h-96 w-96 rounded-full bg-[#35d07f]/20 blur-3xl" />
      <div className="absolute bottom-0 -left-20 h-96 w-96 rounded-full bg-[#4aa8ff]/15 blur-3xl" />
      <div className="absolute -bottom-24 right-1/4 h-80 w-80 rounded-full bg-[#ffce3d]/15 blur-3xl snk-float" />
      <div className="absolute top-10 left-1/3 h-64 w-64 rounded-full bg-[#ff8a3d]/15 blur-3xl" />
    </div>
  );
}

function Marble({ color, className = '' }: { color: string; className?: string }) {
  return (
    <span
      className={`inline-block h-8 w-8 rounded-full border-2 border-white/40 shadow-lg ${className}`}
      style={{
        background: `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85) 0%, ${color} 45%, rgba(0,0,0,0.25) 130%)`,
        boxShadow: `0 8px 18px -6px ${color}88, inset 0 -4px 6px rgba(0,0,0,0.25)`,
      }}
    />
  );
}

const MARBLES: Record<Color, string> = {
  red: '#ff5a5a',
  green: '#35d07f',
  yellow: '#ffce3d',
  blue: '#4aa8ff',
};

function Home({
  session,
  setSession,
  joinCode,
  setJoinCode,
  socket,
  connected,
  error,
}: {
  session: SessionInfo | null;
  setSession: (session: SessionInfo) => void;
  joinCode: string;
  setJoinCode: (v: string) => void;
  socket: GameSocketApi;
  connected: boolean;
  error: string | null;
}) {
  const [guestName, setGuestName] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const nameChanged =
    session?.kind === 'guest' &&
    guestName.trim().length > 0 &&
    guestName.trim() !== (session.name ?? '');
  const identityReady =
    !!session?.name && connected && !nameSaving;

  const saveGuestName = async () => {
    if (session?.kind !== 'guest' || !nameChanged || nameSaving) return;
    const candidate = guestName.trim();
    if (!/^[A-Za-z0-9_]{3,20}$/.test(candidate)) {
      setNameError('Use 3–20 letters, numbers, or underscores.');
      return;
    }
    setNameSaving(true);
    setNameError(null);
    try {
      const updated = await renameGuest(candidate);
      setSession(updated);
      await socket.refreshSession();
      setGuestName('');
    } catch (saveError) {
      if (saveError instanceof SessionApiError && saveError.status === 401) {
        const refreshed = await ensureSession();
        if (refreshed) {
          setSession(refreshed);
          setGuestName('');
          setNameError('Your session expired. A new guest identity was created.');
          return;
        }
      }
      setNameError(
        saveError instanceof Error ? saveError.message : 'Could not change name',
      );
    } finally {
      setNameSaving(false);
    }
  };

  const start = () => {
    if (!identityReady) return;
    socket.createRoom();
  };
  const join = () => {
    if (joinCode.trim().length < 6 || !identityReady) return;
    socket.joinRoom(joinCode.trim().toUpperCase());
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#2a1848] via-snk-bg to-[#140a24]">
      <ArenaBackdrop />

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center px-6 py-12">
        <div className="mb-9 text-center">
          <div className="flex items-center justify-center gap-3">
            <Marble color={MARBLES.red} className="snk-float" />
            <Marble color={MARBLES.green} className="snk-float-slow -translate-y-1" />
            <div className="mx-1 flex h-16 w-16 -rotate-6 items-center justify-center rounded-2xl bg-gradient-to-br from-[#35d07f] via-[#ffd257] to-[#ff5a5a] shadow-glow-gold">
              <span className="text-3xl leading-none drop-shadow-[0_3px_6px_rgba(0,0,0,0.35)]">🐍</span>
            </div>
            <Marble color={MARBLES.yellow} className="snk-float-slow -translate-y-2" />
            <Marble color={MARBLES.blue} className="snk-float" />
          </div>

          <h1 className="mt-6 bg-gradient-to-br from-[#ffe9a8] via-[#8ce99a] to-[#ff8a3d] bg-clip-text font-display text-[3.2rem] font-bold leading-none tracking-tight text-transparent drop-shadow-[0_8px_24px_rgba(53,208,127,0.35)] sm:text-[3.6rem]">
            Snakes &amp; Ladders
          </h1>
          <p className="mt-2 text-sm font-bold uppercase tracking-[0.24em] text-[#ffc96b]">
            Climb · slide · race to 100
          </p>
          <p className="mx-auto mt-4 max-w-sm text-[15px] leading-relaxed text-snk-muted">
            A colourful classic for 2–4 friends. Roll the dice, ride the ladders, dodge the
            snakes, and be first to square 100.
          </p>
        </div>

        <div className="snk-glass w-full rounded-3xl p-7">
          {error && (
            <div className="mb-4 rounded-xl border border-red-400/30 bg-red-500/15 px-4 py-2.5 text-sm font-semibold text-red-200">
              {error}
            </div>
          )}
          {!connected && (
            <div className="mb-4 rounded-xl border border-amber-300/30 bg-amber-400/10 px-4 py-2.5 text-sm font-semibold text-amber-200">
              Connecting to server…
            </div>
          )}

          <label className="block text-xs font-bold uppercase tracking-widest text-snk-muted">
            Playing as
          </label>
          <div className="mt-2 rounded-xl border border-white/10 bg-white/[0.07] p-3">
            {session?.kind === 'guest' ? (
              <>
                <div className="flex items-center gap-2">
                  <input
                    aria-label="Guest name"
                    value={guestName}
                    onChange={(event) => {
                      setGuestName(event.target.value);
                      setNameError(null);
                    }}
                    minLength={3}
                    maxLength={20}
                    placeholder={session.name ?? ''}
                    disabled={nameSaving}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') void saveGuestName();
                    }}
                    className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/10 px-3 py-2 text-sm font-semibold text-snk-ink outline-none transition focus:border-snk-accent/60 focus:ring-2 focus:ring-snk-accent/20 disabled:opacity-60"
                  />
                  <GuestBadge />
                  <button
                    type="button"
                    onClick={() => void saveGuestName()}
                    disabled={!nameChanged || nameSaving}
                    className="rounded-lg bg-snk-accent px-3 py-2 text-xs font-bold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {nameSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <span className="text-xs text-snk-muted/70">
                    3–20 letters, numbers, or underscores
                  </span>
                  <a
                    href={ACCOUNT_URL}
                    className="flex-shrink-0 text-xs font-bold text-snk-accent underline-offset-2 hover:underline"
                  >
                    Sign in
                  </a>
                </div>
              </>
            ) : session?.needsUsername ? (
              <div className="flex items-center justify-between gap-3 px-1 py-1">
                <span className="text-sm text-snk-muted">Choose a username to play</span>
                <a
                  href={ACCOUNT_URL}
                  className="flex-shrink-0 text-xs font-bold text-snk-accent underline-offset-2 hover:underline"
                >
                  Choose name
                </a>
              </div>
            ) : session?.name ? (
              <div className="flex items-center justify-between gap-3 px-1 py-1">
                <span className="truncate text-sm font-semibold text-snk-ink">
                  {session.name}
                </span>
                <a
                  href={ACCOUNT_URL}
                  className="flex-shrink-0 text-xs font-bold text-snk-accent underline-offset-2 hover:underline"
                >
                  Account
                </a>
              </div>
            ) : (
              <span className="text-sm text-snk-muted/50">Loading…</span>
            )}
          </div>
          {nameError && (
            <p className="mt-2 text-xs font-semibold text-red-300">{nameError}</p>
          )}

          <button
            onClick={start}
            disabled={!identityReady}
            className="snk-gradient mt-4 w-full rounded-xl bg-gradient-to-r from-[#35d07f] via-[#ffd257] to-[#ff8a3d] py-3.5 text-sm font-bold tracking-wide text-[#1a1020] shadow-glow transition hover:brightness-110 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-35 disabled:saturate-0"
          >
            Create game room
          </button>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-white/10" />
            <span className="text-[11px] font-bold uppercase tracking-widest text-snk-muted/70">or join</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <div className="flex gap-2">
            <input
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              maxLength={6}
              placeholder="CODE"
              className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/[0.07] px-4 py-3 text-center font-mono text-lg font-bold tracking-[0.35em] text-snk-ink uppercase outline-none transition placeholder:text-snk-muted/50 focus:border-snk-accent/60 focus:bg-white/[0.1] focus:ring-2 focus:ring-snk-accent/20"
            />
            <button
              onClick={join}
              disabled={!identityReady || joinCode.trim().length < 6}
              className="rounded-xl bg-gradient-to-br from-white/15 to-white/5 px-7 py-3 text-sm font-bold text-snk-ink ring-1 ring-white/15 transition hover:bg-white/20 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Join
            </button>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
          {[
            { icon: '🎲', text: 'Roll to move' },
            { icon: '🪜', text: 'Climb ladders' },
            { icon: '🐍', text: 'Dodge snakes' },
            { icon: '🏆', text: 'Land on 100' },
          ].map((chip) => (
            <span
              key={chip.text}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1.5 text-xs font-semibold text-snk-muted"
            >
              <span>{chip.icon}</span>
              {chip.text}
            </span>
          ))}
        </div>
        <p className="mt-6 text-center text-[11px] font-medium text-snk-muted/70">
          Free · No downloads · Up to {MAX_PLAYERS} players per room
        </p>
      </div>
    </div>
  );
}

function RoomLobby({
  socket,
  roomCode,
  snapshot,
  myId,
  error,
}: {
  socket: GameSocketApi;
  roomCode: string | null;
  snapshot: NonNullable<ReturnType<typeof useSnapshot>>;
  myId: string;
  error: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const [botDifficulty, setBotDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const me = snapshot.players.find((p) => p.id === myId)!;
  const isHost = snapshot.hostId === myId;
  const readyCount = snapshot.players.filter((p) => p.ready).length;
  const allReady = snapshot.players.length >= 2 && snapshot.players.every((p) => p.ready);
  const roomFull = snapshot.players.length >= MAX_PLAYERS;

  const copyCode = async () => {
    if (!roomCode) return;
    const link = roomShareUrl(roomCode);
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#2a1848] via-snk-bg to-[#140a24]">
      <ArenaBackdrop />

      <div className="relative z-10 mx-auto w-full max-w-2xl px-6 py-12">
        <div className="text-center">
          <span className="snk-glass inline-flex items-center gap-3 rounded-full px-4 py-2 text-sm font-semibold text-snk-ink">
            <span className="text-[10px] font-bold uppercase tracking-widest text-snk-muted">Room</span>
            <button
              onClick={copyCode}
              title="Copy invite link"
              className="rounded-lg bg-gradient-to-br from-[#ffd257] to-[#ff8a3d] px-2.5 py-0.5 font-mono text-base font-bold tracking-[0.2em] text-white shadow-glow-gold hover:brightness-110"
            >
              {roomCode}
            </button>
            <span className="text-xs font-medium text-snk-muted">{copied ? 'Copied link!' : 'tap to copy link'}</span>
          </span>

          <h1 className="mt-6 bg-gradient-to-br from-white via-[#ffd9a0] to-[#8ce99a] bg-clip-text font-display text-4xl font-bold tracking-tight text-transparent">
            Waiting room
          </h1>
          <p className="mt-2 text-sm font-semibold text-snk-muted">
            {snapshot.players.length}/{MAX_PLAYERS} players · {readyCount}/{snapshot.players.length} ready
          </p>
        </div>

        {/* Single lobby card — every control lives here at one uniform size */}
        <div className="snk-glass mt-8 rounded-3xl p-5 sm:p-6">
          <p className="px-1 text-xs font-bold uppercase tracking-widest text-snk-muted">
            Players
          </p>

          {/* Uniform seats: equal-width grid, equal height cards */}
          <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
            {snapshot.players.map((p) => {
              const accent = MARBLES[p.color];
              const isBot = !!p.isBot;
              return (
                <div
                  key={p.id}
                  className={`flex min-h-[68px] items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-3.5 transition ${
                    p.ready ? 'ring-1 ring-[#ffce3d]/50' : ''
                  }`}
                >
                  <span
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-white/40 text-sm font-bold text-white shadow-lg"
                    style={{
                      background: `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85) 0%, ${accent} 45%, rgba(0,0,0,0.3) 130%)`,
                      boxShadow: `0 10px 24px -8px ${accent}aa`,
                    }}
                  >
                    {isBot ? '🤖' : p.name.slice(0, 1).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="truncate text-sm font-bold text-snk-ink">
                      {p.name}
                      {!isBot && p.kind === 'guest' && <GuestBadge className="ml-1.5" />}
                      {p.id === snapshot.hostId && (
                        <span className="ml-1.5 rounded-full bg-[#ffc94d]/20 px-2 py-0.5 text-[10px] font-bold uppercase text-[#ffd98a]">
                          host
                        </span>
                      )}
                      {isBot && (
                        <span className="ml-1.5 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase text-snk-muted">
                          bot
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-snk-muted">
                      {COLOR_LABELS[p.color]} {p.id === myId && '· you'} {!p.connected && !isBot && '· disconnected'}
                    </p>
                    {isBot &&
                      (isHost ? (
                        <select
                          value={p.botDifficulty ?? 'medium'}
                          onChange={(e) =>
                            socket.setBotDifficulty(p.id, e.target.value as 'easy' | 'medium' | 'hard')
                          }
                          aria-label={`Bot difficulty for ${p.name}`}
                          className="mt-1.5 h-8 cursor-pointer rounded-lg border border-white/10 bg-black/20 px-2 text-xs font-bold text-snk-ink outline-none transition hover:bg-black/30 focus:ring-2 focus:ring-[#ffc94d]/50"
                        >
                          <option value="easy" className="bg-[#2c1850]">
                            Easy
                          </option>
                          <option value="medium" className="bg-[#2c1850]">
                            Medium
                          </option>
                          <option value="hard" className="bg-[#2c1850]">
                            Hard
                          </option>
                        </select>
                      ) : (
                        <span className="mt-1.5 inline-block rounded-full bg-white/5 px-2 py-0.5 text-[11px] font-bold capitalize text-snk-muted">
                          {p.botDifficulty ?? 'medium'}
                        </span>
                      ))}
                  </div>
                  {isBot && isHost ? (
                    <button
                      onClick={() => socket.removeBot(p.id)}
                      title={`Remove ${p.name}`}
                      aria-label={`Remove ${p.name}`}
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.05] text-sm text-snk-muted transition hover:border-red-400/40 hover:text-red-300"
                    >
                      ✕
                    </button>
                  ) : (
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold transition ${
                        p.ready ? 'bg-[#ffce3d]/20 text-[#ffe58a]' : 'bg-white/5 text-snk-muted/70'
                      }`}
                    >
                      {p.ready ? 'Ready' : 'Waiting'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Settings — narrow centered rows */}
          <p className="mt-5 px-1 text-xs font-bold uppercase tracking-widest text-snk-muted">
            Game setup
          </p>
          <div className="mx-auto mt-3 grid max-w-xs gap-2.5">
            {isHost &&
              (roomFull ? (
                <div className="flex h-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] px-4 text-xs font-medium text-snk-muted/80">
                  Room is full — remove a bot to change the lineup.
                </div>
              ) : (
                <div className="flex h-11 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-2 pl-3">
                  <span className="shrink-0 text-xs font-bold uppercase tracking-widest text-snk-muted">
                    🤖 Add bot
                  </span>
                  <select
                    value={botDifficulty}
                    onChange={(e) => setBotDifficulty(e.target.value as 'easy' | 'medium' | 'hard')}
                    aria-label="Bot difficulty"
                    className="h-9 min-w-0 flex-1 cursor-pointer rounded-lg border border-white/10 bg-black/20 px-2.5 text-sm font-bold text-snk-ink outline-none transition hover:bg-black/30 focus:ring-2 focus:ring-[#ffc94d]/50"
                  >
                    <option value="easy" className="bg-[#2c1850]">
                      Easy
                    </option>
                    <option value="medium" className="bg-[#2c1850]">
                      Medium
                    </option>
                    <option value="hard" className="bg-[#2c1850]">
                      Hard
                    </option>
                  </select>
                  <button
                    onClick={() => socket.addBot(botDifficulty)}
                    className="h-9 shrink-0 rounded-lg bg-gradient-to-br from-white/20 to-white/5 px-5 text-sm font-bold text-white ring-1 ring-white/20 transition hover:bg-white/15 active:scale-[0.98]"
                  >
                    Add
                  </button>
                </div>
              ))}
            {isHost && snapshot.players.length === 1 && !roomFull && (
              <p className="text-center text-xs font-medium text-snk-muted/80">
                Playing solo? Add 1–3 bots to start instantly — no friends needed.
              </p>
            )}

            {isHost && (
              <label className="flex h-11 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3">
                <span className="shrink-0 text-xs font-bold uppercase tracking-widest text-snk-muted">
                  Turn timer
                </span>
                <select
                  value={snapshot.turnTimeoutMs / 1000}
                  onChange={(e) => socket.setTimeLimit(Number(e.target.value))}
                  className="h-9 min-w-0 flex-1 cursor-pointer rounded-lg border border-white/10 bg-black/20 px-2.5 text-sm font-bold text-snk-ink outline-none transition hover:bg-black/30 focus:ring-2 focus:ring-[#ffc94d]/50"
                >
                  {TURN_TIME_OPTIONS.map((s) => (
                    <option key={s} value={s} className="bg-[#2c1850] text-snk-ink">
                      {s}s
                    </option>
                  ))}
                </select>
                <span className="shrink-0 text-xs font-medium text-snk-muted">per turn</span>
              </label>
            )}
          </div>

          {error && (
            <div className="mt-3 rounded-xl border border-red-400/30 bg-red-500/15 px-4 py-2.5 text-sm font-semibold text-red-200">
              {error}
            </div>
          )}

          {/* Actions — uniform h-12 buttons, equal widths */}
          <div className={`mt-5 grid gap-2.5 ${isHost ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
            <button
              onClick={() => socket.setReady(!me.ready)}
              className={`h-12 w-full rounded-xl text-sm font-bold tracking-wide transition active:scale-[0.98] ${
                me.ready
                  ? 'bg-gradient-to-br from-[#ffd257] to-[#ffb03d] text-[#3a2400] shadow-glow-gold hover:brightness-110'
                  : 'snk-gradient bg-gradient-to-r from-[#35d07f] via-[#ffd257] to-[#ff8a3d] text-[#1a1020] shadow-glow hover:brightness-110'
              }`}
            >
              {me.ready ? 'Unready' : "I'm ready"}
            </button>
            {isHost && (
              <button
                onClick={() => socket.startGame()}
                disabled={!allReady}
                className="h-12 w-full rounded-xl bg-gradient-to-br from-white/20 to-white/5 text-sm font-bold tracking-wide text-white ring-1 ring-white/20 transition hover:bg-white/15 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-35"
              >
                Start game
              </button>
            )}
            <button
              onClick={() => socket.leave()}
              className="h-12 w-full rounded-xl border border-white/10 bg-white/[0.04] text-sm font-bold text-snk-muted transition hover:border-red-400/40 hover:text-red-300"
            >
              Leave
            </button>
          </div>

          {!allReady && snapshot.players.length >= 2 && (
            <p className="mt-3 text-center text-xs font-medium text-snk-muted/80">
              Everyone needs to hit <span className="font-bold text-[#ffe58a]">“I&apos;m ready”</span> to start.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

